import React from 'react';
import { Routes, Route } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './features/auth';
import { NotificationProvider } from './contexts/NotificationContext';
import { ErrorBoundary } from './shared/components';
import { GlobalThemeProvider } from './theme/GlobalThemeProvider';
import Layout from './components/Layout';
import ProtectedRoute from './components/ProtectedRoute';
import AdminRoute from './components/AdminRoute';
import HomePage from './pages/HomePage';
import StoryEditor from './pages/StoryEditor';
import StoryView from './pages/StoryView';
import PublicStories from './pages/PublicStories';
import Dashboard from './pages/Dashboard';
import Profile from './pages/Profile';
import Friends from './pages/Friends';
import AdminLayout from './components/admin/AdminLayout';
import AdminDashboard from './pages/admin/AdminDashboard';
import UserManagement from './pages/admin/UserManagement';
import ContentModeration from './pages/admin/ContentModeration';
import StoryApprovalQueue from './pages/admin/StoryApprovalQueue';
import AppealManagement from './pages/admin/AppealManagement';
import Reports from './pages/admin/Reports';
import ModerationDashboard from './pages/admin/ModerationDashboard';
import AISettings from './pages/admin/AISettings';
import ImageSettings from './pages/admin/ImageSettings';
import MyStoryStatuses from './pages/MyStoryStatuses';
import ContentGuidelines from './pages/ContentGuidelines';
import { LoadingSpinner } from './shared/components';
import './i18n';

function AppContent() {
  const { isAuthenticated, initialized, isLoading } = useAuth();


  if (!initialized || isLoading) {
    return <LoadingSpinner overlay message="Initializing BookKids..." />;
  }

  return (
    <ErrorBoundary>
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 8000,
          style: {
            background: 'var(--color-white)',
            color: 'var(--color-gray-800)',
            borderRadius: 'var(--border-radius-lg)',
            boxShadow: 'var(--shadow-lg)',
          },
        }}
      />
      
      <Routes>
        <Route path="/" element={<Layout />}>
          <Route index element={<HomePage />} />
          <Route path="/stories" element={<PublicStories />} />
          <Route path="/story/:id" element={<StoryView />} />
          <Route path="/story/new" element={<StoryEditor />} />
          
          {/* Protected routes */}
          <Route path="/dashboard" element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          } />
          <Route path="/story/edit/:id" element={
            <ProtectedRoute>
              <StoryEditor />
            </ProtectedRoute>
          } />
          <Route path="/profile" element={
            <ProtectedRoute>
              <Profile />
            </ProtectedRoute>
          } />
          <Route path="/friends" element={
            <ProtectedRoute>
              <Friends />
            </ProtectedRoute>
          } />
          <Route path="/my-stories" element={
            <ProtectedRoute>
              <MyStoryStatuses />
            </ProtectedRoute>
          } />
          <Route path="/guidelines" element={<ContentGuidelines />} />
          
          {/* Admin routes */}
          <Route path="/admin" element={
            <AdminRoute>
              <AdminLayout />
            </AdminRoute>
          }>
            <Route index element={<AdminDashboard />} />
            <Route path="analytics" element={<Reports />} />
            <Route path="users" element={<UserManagement />} />
            <Route path="content" element={<ContentModeration />} />
            <Route path="stories" element={<StoryApprovalQueue />} />
            <Route path="appeals" element={<AppealManagement />} />
            <Route path="reports" element={<Reports />} />
            <Route path="moderation" element={<ModerationDashboard />} />
            <Route path="ai-settings" element={<AISettings />} />
            <Route path="settings" element={<ImageSettings />} />
          </Route>
        </Route>
      </Routes>
    </ErrorBoundary>
  );
}

function App() {
  return (
    <GlobalThemeProvider>
      <AuthProvider>
        <NotificationProvider>
          <AppContent />
        </NotificationProvider>
      </AuthProvider>
    </GlobalThemeProvider>
  );
}

export default App;